import * as z from 'zod';

import * as Types from './graphql';

type Properties<T> = Required<{
  [K in keyof T]: z.ZodType<T[K]>;
}>;

type definedNonNullAny = {};

export const isDefinedNonNullAny = (v: any): v is definedNonNullAny => v !== undefined && v !== null;

export const definedNonNullAnySchema = z.any().refine((v) => isDefinedNonNullAny(v));

export const ModelSortDirectionSchema = z.enum(Types.ModelSortDirection);

export function CalendarWidgetInfoInputSchema(): z.ZodObject<Properties<Types.CalendarWidgetInfoInput>> {
  return z.object({
    endDate: z.string().nullish(),
    href: z.string().nullish(),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
    surface: z.string().nullish(),
    tournamentName: z.string().nullish(),
    tournamentPlace: z.string().nullish(),
    tournamentType: z.string().nullish(),
  });
}

export function CreateEditorialDataInputSchema(): z.ZodObject<Properties<Types.CreateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any(),
    seasonId: z.string(),
  });
}

export function CreateSdapiCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.CreateSdapiCalendarWidgetInput>> {
  return z.object({
    circuitCalendarLink: z.string().nullish(),
    circuitType: z.string(),
    filterList: z.array(z.string().nullable()).nullish(),
    tournamentFilter: z.string(),
    tournaments: z.array(z.lazy(() => CalendarWidgetInfoInputSchema().nullable())).nullish(),
  });
}

export function CreateSdapiCompetitionInputSchema(): z.ZodObject<Properties<Types.CreateSdapiCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    competitionJson: z.any(),
    seasonAndCompId: z.string(),
    seasonAndTournamentType: z.string(),
    seasonId: z.string(),
    sex: z.string().nullish(),
    tournamentId: z.string(),
    tournamentType: z.string(),
    type: z.string().nullish(),
  });
}

export function CreateSdapiLiveScoreWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateSdapiLiveScoreWidgetInput>
> {
  return z.object({
    ctaUrls: z.lazy(() => LiveScoreWidgetCtaUrlInputSchema().nullish()),
    id: z.string().nullish(),
    liveTournaments: z.array(z.lazy(() => LiveScoreWidgetTournamentInputSchema().nullable())).nullish(),
  });
}

export function CreateSdapiMatchInputSchema(): z.ZodObject<Properties<Types.CreateSdapiMatchInput>> {
  return z.object({
    competitionId: z.string(),
    date: z.number().nullish(),
    matchId: z.string(),
    matchLiveJson: z.any().nullish(),
    matchScheduleJson: z.any().nullish(),
    playerA1Id: z.string().nullish(),
    playerA2Id: z.string().nullish(),
    playerB1Id: z.string().nullish(),
    playerB2Id: z.string().nullish(),
    round: z.string().nullish(),
    roundName: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    status: z.string().nullish(),
    tournamentId: z.string().nullish(),
  });
}

export function CreateSdapiPlayerInputSchema(): z.ZodObject<Properties<Types.CreateSdapiPlayerInput>> {
  return z.object({
    playerId: z.string(),
    playerJson: z.any().nullish(),
    playerStatsJson: z.any().nullish(),
  });
}

export function CreateSdapiRankingInputSchema(): z.ZodObject<Properties<Types.CreateSdapiRankingInput>> {
  return z.object({
    circuitType: z.string(),
    playerId: z.string(),
    rank: z.number(),
    rankingJson: z.any(),
  });
}

export function CreateSdapiRankingWidgetInputSchema(): z.ZodObject<Properties<Types.CreateSdapiRankingWidgetInput>> {
  return z.object({
    circuitType: z.string(),
    rankings: z.array(z.lazy(() => RankingWidgetInfoInputSchema().nullable())).nullish(),
  });
}

export function CreateSdapiResultsCategoriesWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateSdapiResultsCategoriesWidgetInput>
> {
  return z.object({
    adv: z.lazy(() => ResultsCategoriesWidgetAdvInputSchema().nullish()),
    categories: z.array(z.lazy(() => ResultsCategoriesWidgetInfoInputSchema().nullable())).nullish(),
    seasonId: z.string(),
    tournamentId: z.string(),
    tournamentType: z.string(),
  });
}

export function CreateSdapiResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateSdapiResultsContainerWidgetInput>
> {
  return z.object({
    seasonId: z.string(),
    ssi: z.array(z.string().nullable()).nullish(),
    tournamentId: z.string(),
    tournamentType: z.string(),
  });
}

export function CreateSdapiResultsWidgetInputSchema(): z.ZodObject<Properties<Types.CreateSdapiResultsWidgetInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    competitionId: z.string(),
    date: z.string().nullish(),
    day: z.string(),
    matches: z.array(z.lazy(() => ResultsWidgetMatchInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateSdapiSeasonInputSchema(): z.ZodObject<Properties<Types.CreateSdapiSeasonInput>> {
  return z.object({
    editorialData: z.any().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonId: z.string(),
  });
}

export function CreateSdapiTournamentDataWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateSdapiTournamentDataWidgetInput>
> {
  return z.object({
    data: z.any().nullish(),
    messageType: z.string(),
    seasonAndTournamentType: z.string(),
    seasonId: z.string(),
    tournamentId: z.string(),
    tournamentType: z.string().nullish(),
  });
}

export function CreateSdapiTournamentInputSchema(): z.ZodObject<Properties<Types.CreateSdapiTournamentInput>> {
  return z.object({
    circuitType: z.string(),
    editorialDataId: z.string(),
    seasonAndTournamentType: z.string(),
    seasonId: z.string(),
    tournamentId: z.string(),
    tournamentJson: z.any(),
    type: z.string(),
  });
}

export function DeleteEditorialDataInputSchema(): z.ZodObject<Properties<Types.DeleteEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteSdapiCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiCalendarWidgetInput>> {
  return z.object({
    circuitType: z.string(),
    tournamentFilter: z.string(),
  });
}

export function DeleteSdapiCompetitionInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteSdapiLiveScoreWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteSdapiLiveScoreWidgetInput>
> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteSdapiMatchInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiMatchInput>> {
  return z.object({
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteSdapiPlayerInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiPlayerInput>> {
  return z.object({
    playerId: z.string(),
  });
}

export function DeleteSdapiRankingInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiRankingInput>> {
  return z.object({
    circuitType: z.string(),
    rank: z.number(),
  });
}

export function DeleteSdapiRankingWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiRankingWidgetInput>> {
  return z.object({
    circuitType: z.string(),
  });
}

export function DeleteSdapiResultsCategoriesWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteSdapiResultsCategoriesWidgetInput>
> {
  return z.object({
    seasonId: z.string(),
    tournamentId: z.string(),
    tournamentType: z.string(),
  });
}

export function DeleteSdapiResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteSdapiResultsContainerWidgetInput>
> {
  return z.object({
    seasonId: z.string(),
    tournamentId: z.string(),
    tournamentType: z.string(),
  });
}

export function DeleteSdapiResultsWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiResultsWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    day: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteSdapiSeasonInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiSeasonInput>> {
  return z.object({
    seasonId: z.string(),
  });
}

export function DeleteSdapiTournamentDataWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteSdapiTournamentDataWidgetInput>
> {
  return z.object({
    seasonAndTournamentType: z.string(),
    tournamentId: z.string(),
  });
}

export function DeleteSdapiTournamentInputSchema(): z.ZodObject<Properties<Types.DeleteSdapiTournamentInput>> {
  return z.object({
    seasonAndTournamentType: z.string(),
    tournamentId: z.string(),
  });
}

export function LiveScoreWidgetCtaUrlInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetCtaUrlInput>> {
  return z.object({
    atpCalendarUrl: z.string().nullish(),
    atpRankingUrl: z.string().nullish(),
    wtaCalendarUrl: z.string().nullish(),
    wtaRankingUrl: z.string().nullish(),
  });
}

export function LiveScoreWidgetMatchInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetMatchInput>> {
  return z.object({
    firstPosition: z.lazy(() => LiveScoreWidgetPositionInputSchema().nullish()),
    resultType: z.string().nullish(),
    roundName: z.string().nullish(),
    secondPosition: z.lazy(() => LiveScoreWidgetPositionInputSchema().nullish()),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function LiveScoreWidgetPlayerInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetPlayerInput>> {
  return z.object({
    name: z.string().nullish(),
    nationality: z.string().nullish(),
    seed: z.string().nullish(),
  });
}

export function LiveScoreWidgetPositionInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetPositionInput>> {
  return z.object({
    isServing: z.boolean().nullish(),
    players: z.array(z.lazy(() => LiveScoreWidgetPlayerInputSchema().nullable())).nullish(),
    score: z.array(z.lazy(() => LiveScoreWidgetScoreInputSchema().nullable())).nullish(),
  });
}

export function LiveScoreWidgetScoreInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetScoreInput>> {
  return z.object({
    isWinner: z.boolean().nullish(),
    set: z.string().nullish(),
    tie: z.string().nullish(),
  });
}

export function LiveScoreWidgetTournamentInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetTournamentInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    circuitType: z.string().nullish(),
    competitionId: z.string().nullish(),
    matches: z.array(z.lazy(() => LiveScoreWidgetMatchInputSchema().nullable())).nullish(),
    resultsUrl: z.string().nullish(),
    seasonId: z.string().nullish(),
    tournamentId: z.string().nullish(),
    tournamentName: z.string().nullish(),
    tournamentType: z.string().nullish(),
  });
}

export function ModelBooleanFilterInputSchema(): z.ZodObject<Properties<Types.ModelBooleanFilterInput>> {
  return z.object({
    eq: z.boolean().nullish(),
    ne: z.boolean().nullish(),
  });
}

export function ModelEditorialDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelEditorialDataFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    editorialJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelEditorialDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelFloatFilterInputSchema(): z.ZodObject<Properties<Types.ModelFloatFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelIDFilterInputSchema(): z.ZodObject<Properties<Types.ModelIDFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelIDKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIDKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function ModelIntFilterInputSchema(): z.ZodObject<Properties<Types.ModelIntFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelIntKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIntKeyConditionInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
  });
}

export function ModelSdapiCalendarWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiCalendarWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiCalendarWidgetFilterInputSchema().nullable())).nullish(),
    circuitCalendarLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    circuitType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    filterList: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiCalendarWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiCalendarWidgetFilterInputSchema().nullable())).nullish(),
    tournamentFilter: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelSdapiCompetitionFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiCompetitionFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiCompetitionFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiCompetitionFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiCompetitionFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonAndTournamentType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    sex: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelSdapiLiveScoreWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiLiveScoreWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiLiveScoreWidgetFilterInputSchema().nullable())).nullish(),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiLiveScoreWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiLiveScoreWidgetFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelSdapiMatchFilterInputSchema(): z.ZodObject<Properties<Types.ModelSdapiMatchFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiMatchFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    date: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchLiveJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchScheduleJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiMatchFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiMatchFilterInputSchema().nullable())).nullish(),
    playerA1Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerA2Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerB1Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerB2Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    round: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    roundName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSdapiPlayerFilterInputSchema(): z.ZodObject<Properties<Types.ModelSdapiPlayerFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiPlayerFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelSdapiPlayerFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiPlayerFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    playerStatsJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelSdapiRankingFilterInputSchema(): z.ZodObject<Properties<Types.ModelSdapiRankingFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiRankingFilterInputSchema().nullable())).nullish(),
    circuitType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiRankingFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    rank: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    rankingJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelSdapiRankingWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiRankingWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiRankingWidgetFilterInputSchema().nullable())).nullish(),
    circuitType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiRankingWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiRankingWidgetFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelSdapiResultsCategoriesWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsCategoriesWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiResultsCategoriesWidgetFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelSdapiResultsCategoriesWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiResultsCategoriesWidgetFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyConditionInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyConditionInput>
> {
  return z.object({
    beginsWith: z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullish()),
    between: z
      .array(z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullable()))
      .nullish(),
    eq: z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullish()),
    ge: z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullish()),
    gt: z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullish()),
    le: z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullish()),
    lt: z.lazy(() => ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema().nullish()),
  });
}

export function ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsCategoriesWidgetPrimaryCompositeKeyInput>
> {
  return z.object({
    seasonId: z.string().nullish(),
    tournamentType: z.string().nullish(),
  });
}

export function ModelSdapiResultsContainerWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsContainerWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelSdapiResultsContainerWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    ssi: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSdapiResultsContainerWidgetPrimaryCompositeKeyConditionInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsContainerWidgetPrimaryCompositeKeyConditionInput>
> {
  return z.object({
    beginsWith: z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullish()),
    between: z
      .array(z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullable()))
      .nullish(),
    eq: z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullish()),
    ge: z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullish()),
    gt: z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullish()),
    le: z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullish()),
    lt: z.lazy(() => ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema().nullish()),
  });
}

export function ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsContainerWidgetPrimaryCompositeKeyInput>
> {
  return z.object({
    seasonId: z.string().nullish(),
    tournamentType: z.string().nullish(),
  });
}

export function ModelSdapiResultsWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiResultsWidgetFilterInputSchema().nullable())).nullish(),
    categoryName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    day: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiResultsWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiResultsWidgetFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSdapiResultsWidgetPrimaryCompositeKeyConditionInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsWidgetPrimaryCompositeKeyConditionInput>
> {
  return z.object({
    beginsWith: z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullish()),
    between: z.array(z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullable())).nullish(),
    eq: z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullish()),
    ge: z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullish()),
    gt: z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullish()),
    le: z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullish()),
    lt: z.lazy(() => ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema().nullish()),
  });
}

export function ModelSdapiResultsWidgetPrimaryCompositeKeyInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiResultsWidgetPrimaryCompositeKeyInput>
> {
  return z.object({
    competitionId: z.string().nullish(),
    seasonId: z.string().nullish(),
  });
}

export function ModelSdapiSeasonFilterInputSchema(): z.ZodObject<Properties<Types.ModelSdapiSeasonFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiSeasonFilterInputSchema().nullable())).nullish(),
    editorialData: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiSeasonFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiSeasonFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSdapiTournamentDataWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiTournamentDataWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiTournamentDataWidgetFilterInputSchema().nullable())).nullish(),
    data: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    messageType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiTournamentDataWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiTournamentDataWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndTournamentType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelSdapiTournamentFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSdapiTournamentFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSdapiTournamentFilterInputSchema().nullable())).nullish(),
    circuitType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSdapiTournamentFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSdapiTournamentFilterInputSchema().nullable())).nullish(),
    seasonAndTournamentType: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    tournamentJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    type: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelStringFilterInputSchema(): z.ZodObject<Properties<Types.ModelStringFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelStringKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelStringKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function RankingWidgetInfoInputSchema(): z.ZodObject<Properties<Types.RankingWidgetInfoInput>> {
  return z.object({
    nationality: z.string().nullish(),
    playerName: z.string().nullish(),
    points: z.string().nullish(),
    rank: z.number().nullish(),
    trend: z.string().nullish(),
  });
}

export function ResultWidgetPlayerInputSchema(): z.ZodObject<Properties<Types.ResultWidgetPlayerInput>> {
  return z.object({
    name: z.string().nullish(),
    nationality: z.string().nullish(),
    seed: z.string().nullish(),
  });
}

export function ResultWidgetPositionInputSchema(): z.ZodObject<Properties<Types.ResultWidgetPositionInput>> {
  return z.object({
    isServing: z.boolean().nullish(),
    isWinner: z.boolean().nullish(),
    players: z.array(z.lazy(() => ResultWidgetPlayerInputSchema().nullable())).nullish(),
    score: z.array(z.lazy(() => ResultWidgetScoreInputSchema().nullable())).nullish(),
  });
}

export function ResultWidgetScoreInputSchema(): z.ZodObject<Properties<Types.ResultWidgetScoreInput>> {
  return z.object({
    isWinner: z.boolean().nullish(),
    set: z.string().nullish(),
    tie: z.string().nullish(),
  });
}

export function ResultsCategoriesWidgetAdvInputSchema(): z.ZodObject<
  Properties<Types.ResultsCategoriesWidgetAdvInput>
> {
  return z.object({
    image: z.string().nullish(),
    link: z.string().nullish(),
  });
}

export function ResultsCategoriesWidgetInfoInputSchema(): z.ZodObject<
  Properties<Types.ResultsCategoriesWidgetInfoInput>
> {
  return z.object({
    dates: z.array(z.string().nullable()).nullish(),
    name: z.string().nullish(),
  });
}

export function ResultsWidgetMatchInputSchema(): z.ZodObject<Properties<Types.ResultsWidgetMatchInput>> {
  return z.object({
    firstPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    resultType: z.string().nullish(),
    roundName: z.string().nullish(),
    secondPosition: z.lazy(() => ResultWidgetPositionInputSchema().nullish()),
    startDate: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function UpdateEditorialDataInputSchema(): z.ZodObject<Properties<Types.UpdateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any().nullish(),
    seasonId: z.string(),
  });
}

export function UpdateSdapiCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiCalendarWidgetInput>> {
  return z.object({
    circuitCalendarLink: z.string().nullish(),
    circuitType: z.string(),
    filterList: z.array(z.string().nullable()).nullish(),
    tournamentFilter: z.string(),
    tournaments: z.array(z.lazy(() => CalendarWidgetInfoInputSchema().nullable())).nullish(),
  });
}

export function UpdateSdapiCompetitionInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    competitionJson: z.any().nullish(),
    seasonAndCompId: z.string().nullish(),
    seasonAndTournamentType: z.string().nullish(),
    seasonId: z.string(),
    sex: z.string().nullish(),
    tournamentId: z.string().nullish(),
    tournamentType: z.string().nullish(),
    type: z.string().nullish(),
  });
}

export function UpdateSdapiLiveScoreWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateSdapiLiveScoreWidgetInput>
> {
  return z.object({
    ctaUrls: z.lazy(() => LiveScoreWidgetCtaUrlInputSchema().nullish()),
    id: z.string(),
    liveTournaments: z.array(z.lazy(() => LiveScoreWidgetTournamentInputSchema().nullable())).nullish(),
  });
}

export function UpdateSdapiMatchInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiMatchInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    date: z.number().nullish(),
    matchId: z.string(),
    matchLiveJson: z.any().nullish(),
    matchScheduleJson: z.any().nullish(),
    playerA1Id: z.string().nullish(),
    playerA2Id: z.string().nullish(),
    playerB1Id: z.string().nullish(),
    playerB2Id: z.string().nullish(),
    round: z.string().nullish(),
    roundName: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
    status: z.string().nullish(),
    tournamentId: z.string().nullish(),
  });
}

export function UpdateSdapiPlayerInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiPlayerInput>> {
  return z.object({
    playerId: z.string(),
    playerJson: z.any().nullish(),
    playerStatsJson: z.any().nullish(),
  });
}

export function UpdateSdapiRankingInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiRankingInput>> {
  return z.object({
    circuitType: z.string(),
    playerId: z.string().nullish(),
    rank: z.number(),
    rankingJson: z.any().nullish(),
  });
}

export function UpdateSdapiRankingWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiRankingWidgetInput>> {
  return z.object({
    circuitType: z.string(),
    rankings: z.array(z.lazy(() => RankingWidgetInfoInputSchema().nullable())).nullish(),
  });
}

export function UpdateSdapiResultsCategoriesWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateSdapiResultsCategoriesWidgetInput>
> {
  return z.object({
    adv: z.lazy(() => ResultsCategoriesWidgetAdvInputSchema().nullish()),
    categories: z.array(z.lazy(() => ResultsCategoriesWidgetInfoInputSchema().nullable())).nullish(),
    seasonId: z.string(),
    tournamentId: z.string(),
    tournamentType: z.string(),
  });
}

export function UpdateSdapiResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateSdapiResultsContainerWidgetInput>
> {
  return z.object({
    seasonId: z.string(),
    ssi: z.array(z.string().nullable()).nullish(),
    tournamentId: z.string(),
    tournamentType: z.string(),
  });
}

export function UpdateSdapiResultsWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiResultsWidgetInput>> {
  return z.object({
    categoryName: z.string().nullish(),
    competitionId: z.string(),
    date: z.string().nullish(),
    day: z.string(),
    matches: z.array(z.lazy(() => ResultsWidgetMatchInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateSdapiSeasonInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiSeasonInput>> {
  return z.object({
    editorialData: z.any().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonId: z.string(),
  });
}

export function UpdateSdapiTournamentDataWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateSdapiTournamentDataWidgetInput>
> {
  return z.object({
    data: z.any().nullish(),
    messageType: z.string().nullish(),
    seasonAndTournamentType: z.string(),
    seasonId: z.string().nullish(),
    tournamentId: z.string(),
    tournamentType: z.string().nullish(),
  });
}

export function UpdateSdapiTournamentInputSchema(): z.ZodObject<Properties<Types.UpdateSdapiTournamentInput>> {
  return z.object({
    circuitType: z.string().nullish(),
    editorialDataId: z.string().nullish(),
    seasonAndTournamentType: z.string(),
    seasonId: z.string().nullish(),
    tournamentId: z.string(),
    tournamentJson: z.any().nullish(),
    type: z.string().nullish(),
  });
}
